import React, { useEffect, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import styled from 'styled-components';

import { useIsMobile } from 'hooks/common';
import ModalBase from 'components/Modal/ModalBase';

import {
  FixedTicket,
  UnfixedTicket,
  BookingConnection,
  BookingState,
  TicketState,
  BookingEdge,
  OrderState,
} from 'types/api';

import { GET_BOOKING_LIST } from './graphQL';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';

import AttendanceCheckModalHeader from './AttendanceCheckModal/AttendanceCheckModalHeader';
import UserListContent from './AttendanceCheckModal/UserListContent';

import { useMutationTicketsSale } from 'containers/SaleDetail/graphQL/hooks/useMutationTicketsSale';
import { fetchAllAtOnce } from 'utils/fetchAllAtOnce';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import HackleManager from 'utils/hackle';
import { BookingTermType } from './graphQL/types';
import Loading from 'components/Loading/Loading';
import ListLoadingSpinner from './components/ListLoadingSpinner';
import EmptyList from './components/EmptyList';
import CancelBookingConfirmModal from './CancelBookingConfirmModal';
import { BookingItemsFilter } from 'types/booking.types';

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;

  height: calc(100% - 104px);

  overflow: auto;
`;

const ModalContent = styled.div`
  width: 100%;
  height: 100%;

  overflow-y: hidden;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 16;

  padding: 16px 20px 32px;
  margin-top: auto;

  border-top: 1px solid;
  border-color: rgba(234, 235, 236, 1);

  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #eaebec;
  background-color: #fff;
`;

const ModalFooterButton = styled.button<{ $isActive: boolean }>`
  height: 48px;
  padding: 0 24px;

  border-radius: 12px;

  line-height: 48px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  color: #dbdcdf;
  background-color: #f4f4f5;

  cursor: pointer;

  ${({ $isActive }) =>
    $isActive &&
    ` &[data-type="absent"] {
      background-color: #EAEBEC;
      color: #46474C;
    }
  
    &[data-type="attendance"] {
      background-color: #0071E9;
      color : #fff;
    }`}
`;

interface Props {
  handleModal: () => void;
  item: UnfixedTicket | FixedTicket;
  handleRefetchBookingList: () => void;
  refetchUnfixedTicketCount: () => void;
}

function AttendanceCheckModal({
  handleModal,
  item,
  handleRefetchBookingList,
  refetchUnfixedTicketCount,
}: Props) {
  const isMobile = useIsMobile();

  const isFixedTicket = 'minimumQuota' in item ? true : false;

  const [attendanceCheckedList, setAttendanceCheckedList] = useState<
    Record<string, string[]>
  >({});

  const hostId = useUserInformation(state => state.userInfo.me.host.id);
  const client = useApolloClient();

  const productId = !isFixedTicket
    ? (item as UnfixedTicket).bookingItem.booking.info.product.id
    : (item as FixedTicket).product.id;

  const startTime = !isFixedTicket
    ? (item as UnfixedTicket).scheduleTerm.startedAt
    : (item as FixedTicket).term.startedAt;

  const endTime = !isFixedTicket
    ? (item as UnfixedTicket).scheduleTerm.endedAt
    : (item as FixedTicket).term.endedAt;

  const fixedTicketFilter = {
    scheduleId: item.id,
  };

  const unfixedTicketFilter = {
    termTypes: BookingTermType.STARTING,
    term: {
      endedAt: startTime,
      startedAt: startTime,
    },
    productIds: [productId],
  };

  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const [bookingItemsFilter, setBookingItemsFilter] =
    useState<BookingItemsFilter>({
      page: 1,
      size: 200,
      filter: {
        hostId: hostId,
        customerSearchKeyword: '',
        ...(!isFixedTicket ? unfixedTicketFilter : fixedTicketFilter),
      },
    });

  const {
    handleRestoreTickets, // 예약 확정 취소 여러개 - 조율형
    handleUseTickets, // 사용 처리 여러개
    handleRestoreUseTickets, // 사용 처리 취소 여러개
    handleAbsentTickets, // 불참 처리 여러개
    handleRestoreTicketsAbsence, // 불참 처리 취소 여러개
  } = useMutationTicketsSale();

  const [selectedConfirmedBookingIds, setSelectedConfirmedBookingIds] =
    useState<string[]>([]);

  const [isCancelBookingConfirmModal, setIsCancelBookingConfirmModal] =
    useState(false);

  const hasCheckedAttendance = Object.keys(attendanceCheckedList).length > 0;

  const {
    data: bookingItems,
    loading,
    refetch: bookingListRefetch,
    updateQuery,
  } = useQuery<{
    booking: { bookings: BookingConnection };
  }>(GET_BOOKING_LIST, {
    variables: {
      ...bookingItemsFilter,
    },
    /* 모달을 다시 열 때 캐시된 과거 주문/취소 상태로 처리되지 않도록 항상 서버 조회 */
    fetchPolicy: 'network-only',
  });

  /*
    200명 초과 시 나머지를 한 번에 로드해 교체한다. (페이지 분할 시 누락/중복 이유는 fetchAllAtOnce 참고)
    추가 요청은 no-cache 로 받아 다른 쿼리 캐시를 건드리지 않고, 결과만 이 쿼리에 써 넣는다.
  */
  useEffect(() => {
    const firstPage = bookingItems?.booking?.bookings;

    if (!firstPage?.pageInfo?.hasNextPage) {
      return;
    }

    let isStale = false;

    fetchAllAtOnce(
      firstPage,
      () => isStale,
      async (page, size) => {
        const result = await fetchWithRetry(() =>
          client.query<{ booking: { bookings: BookingConnection } }>({
            query: GET_BOOKING_LIST,
            variables: { ...bookingItemsFilter, page, size },
            fetchPolicy: 'no-cache',
          }),
        );
        return result.data?.booking?.bookings;
      },
    )
      .then(edges => {
        if (isStale) return;

        updateQuery(prev => {
          if (!prev?.booking?.bookings) return prev;

          return {
            booking: {
              ...prev.booking,
              bookings: {
                ...prev.booking.bookings,
                edges,
                pageInfo: {
                  ...prev.booking.bookings.pageInfo,
                  hasNextPage: false,
                },
              },
            },
          };
        });
      })
      .catch(error => {
        console.error('Error fetching all bookings:', error);
      });

    return () => {
      isStale = true;
    };
  }, [bookingItems]);

  useEffect(() => {
    setBookingItemsFilter(pre => ({
      ...pre,
      term: {
        endedAt: startTime,
        startedAt: startTime,
      },
      productIds: [item.id],
    }));
  }, [bookingItems, startTime]);

  const saveSelectedConfirmedBookingIds = (
    selectedConfirmedBookingIds: string[],
  ) => {
    setSelectedConfirmedBookingIds([...selectedConfirmedBookingIds]);
  };

  const handleCancelBookingConfirmModal = () => {
    setIsCancelBookingConfirmModal(prev => !prev);
  };

  const handleCancelBookingConfirm = () => {
    handleRestoreTickets(selectedConfirmedBookingIds, async () => {
      await bookingListRefetch();
      await handleRefetchBookingList();
      await refetchUnfixedTicketCount();
    });

    handleCancelBookingConfirmModal();
  };

  const confirmedBookingTicket = bookingItems?.booking?.bookings?.edges.find(
    ({ node }) => {
      const { tickets } = node;

      return tickets.filter(ticket => {
        if (ticket.confirmedAt || ticket.usedAt || ticket.absentAt) {
          return ticket;
        }
      });
    },
  );

  const filteredConfirmedBookingTicket =
    confirmedBookingTicket?.node.tickets.filter(ticket => {
      if (ticket.confirmedAt || ticket.usedAt || ticket.absentAt) {
        return ticket;
      }
    });

  const confirmedAt =
    filteredConfirmedBookingTicket?.[0]?.confirmedAt; /* 예약 확정일 */
  const usedAt = filteredConfirmedBookingTicket?.[0]?.usedAt; /* 참석 확정일 */
  const absentAt =
    filteredConfirmedBookingTicket?.[0]?.absentAt; /* 불참 확정일 */
  const timeline = { confirmedAt, usedAt, absentAt };

  const canceledOrderStates: string[] = [
    OrderState.CANCELED,
    OrderState.CANCELING,
    OrderState.CANCEL_REQUESTED,
  ];

  const userList =
    bookingItems?.booking?.bookings?.edges.filter(data => {
      if (data.node.status === BookingState.CANCELED) {
        return false;
      }
      if (canceledOrderStates.includes(data.node.order?.status)) {
        return false;
      }
      return true;
    }) || [];

  const findCanceledTicketIndexNoListByNode = (
    bookingNode: (typeof userList)[0]['node'],
  ) => {
    const bookingId = bookingNode?.id;
    const orderData = bookingNode?.order;

    const filteredOrderItem = orderData?.items?.filter(item => {
      if (item.id === bookingId) {
        return item;
      }
    });

    const ticketIndexNo = filteredOrderItem
      ?.map(item => {
        return item?.dependedItems?.map(item => {
          return item?.units?.map(item => {
            return item?.indexNo;
          });
        });
      })
      ?.flat();

    return ticketIndexNo?.flat();
  };

  const checkableList = userList
    .map(({ node: bookingItem }) => {
      /* 불/참석 처리 된 티켓은 제외하고 체크 */
      if (
        [BookingState.USED, BookingState.ABSENT].includes(bookingItem.status)
      ) {
        return false;
      }

      const canceledTicketIndexNoList =
        findCanceledTicketIndexNoListByNode(bookingItem);

      const filteredBookingItem = bookingItem?.items?.filter((item, idx) => {
        const itemIndexNo = item?.indexNo;

        /* 취소요청, 취소완료된 티켓은 제외 */
        if (canceledTicketIndexNoList?.includes(itemIndexNo)) {
          return false;
        }

        const itemTicketList = item?.tickets;

        const confirmedTicketList = itemTicketList?.filter(ticket => {
          if (ticket?.status === TicketState.CONFIRMED) {
            return true;
          }
        });

        return confirmedTicketList?.length > 0 ? true : false;
      });

      return {
        ...bookingItem,
        items: filteredBookingItem,
      };
    })
    .filter(item => {
      if (!item || item?.items?.length === 0) {
        return;
      }

      return item;
    });

  const normalUnfixedCheckAll = (bookingItem: BookingEdge) => {
    const canceledTicketIndexNoList = findCanceledTicketIndexNoListByNode(
      bookingItem.node,
    );

    bookingItem.node.items.forEach((item, idx) => {
      const itemIndexNo = item?.indexNo;
      const itemTicketList = item?.tickets;

      /* 취소요청, 취소완료된 티켓은 제외 */
      if (canceledTicketIndexNoList?.includes(itemIndexNo)) {
        return;
      }

      /* 불/참석 처리 된 티켓은 제외하고 체크 */
      if (
        [BookingState.ABSENT, BookingState.USED].includes(
          bookingItem.node.status,
        )
      ) {
        return;
      }

      itemTicketList?.forEach(ticket => {
        if (ticket?.status === TicketState.CONFIRMED) {
          setAttendanceCheckedList(pre => ({
            ...pre,
            [bookingItem.node.id]: [
              ...(pre[bookingItem.node.id] || []),
              ticket.id,
            ],
          }));
        }
      });
    });
  };

  const multipleUnfixedCheckAll = (bookingItem: BookingEdge) => {
    const realTicketListByItem = bookingItem?.node?.items?.map(item => {
      return item?.tickets?.filter(ticket => {
        return ticket?.scheduleTerm?.startedAt === startTime;
      });
    });

    const findCanceledTicketIndexNoList = () => {
      const bookingId = bookingItem?.node?.id;
      const orderData = bookingItem?.node?.order;

      const filteredOrderItem = orderData?.items?.filter(item => {
        if (item?.id === bookingId) {
          return item;
        }
      });

      const ticketIndexNo = filteredOrderItem
        ?.map(item => {
          return item?.dependedItems?.map(item => {
            return item?.units?.map(item => {
              return item?.indexNo;
            });
          });
        })
        .flat();

      return ticketIndexNo?.flat();
    };

    const canceledTicketIndexNoList = findCanceledTicketIndexNoList();

    const availableTicketList = realTicketListByItem
      ?.filter((dataArray, itemIdx) => {
        /*  취소요청이 있는 티켓은 제외 (indexNo 기준) */
        const itemIndexNo = bookingItem?.node?.items?.[itemIdx]?.indexNo;
        if (canceledTicketIndexNoList?.includes(itemIndexNo)) {
          return;
        }

        return dataArray;
      })
      .flat()
      .filter(data => data.status !== TicketState.CANCELED);

    const ticketIdList = availableTicketList?.map(data => data?.id);

    checkUserHandler(ticketIdList, bookingItem?.node?.id);
  };

  /* 다회권이 아닌 일반 전체 체크박스 선택 핸들러 */
  const checkAllUserHandler = () => {
    if (
      Object.keys(attendanceCheckedList).length === checkableList.length &&
      hasCheckedAttendance
    ) {
      setAttendanceCheckedList({});
    } else {
      userList.forEach(bookingItem => {
        if (bookingItem?.node?.items?.[0]?.tickets?.length > 1) {
          /* 다회권 */
          multipleUnfixedCheckAll(bookingItem);
        } else {
          normalUnfixedCheckAll(bookingItem);
        }
      });
    }
  };

  /* 개별 체크박스 선택 핸들러 */
  const checkUserHandler = (ticketIdList: string[], bookingId: string) => {
    setAttendanceCheckedList(pre => {
      const bookingIdList = Object.keys(pre);

      if (bookingIdList.includes(bookingId)) {
        const newState = { ...pre };
        delete newState[bookingId];
        return newState;
      } else {
        if (ticketIdList?.length > 0) {
          return { ...pre, [bookingId]: ticketIdList };
        }
        return pre;
      }
    });
  };

  // useEffect(() => {
  //   console.log('attendanceCheckedList', attendanceCheckedList);
  // }, [attendanceCheckedList]);

  /*
    처리 직전 최신 주문 상태를 조회해, 취소요청·취소 진행 중인 주문의 티켓을
    체크 목록에서 제외한다. (취소요청된 예약이 사용/불참 처리되는 것 방지)

    화면의 검색 상태와 무관하게 검색어 없이 전체 페이지를 끝까지 조회해 검증한다.
    (검색 중이거나 200명 초과 회차에서 체크해둔 티켓이 검증을 건너뛰는 것 방지)
    조회가 끝내 실패하면 throw되며, 호출부에서 처리를 중단한다. (fail-closed)
  */
  const excludeCancelRequestedTickets = async (
    checkedTicketIdList: string[],
  ) => {
    const fetchPage = async (page: number, size: number) => {
      const result = await fetchWithRetry(() =>
        client.query<{ booking: { bookings: BookingConnection } }>({
          query: GET_BOOKING_LIST,
          variables: {
            ...bookingItemsFilter,
            page,
            size,
            filter: {
              ...bookingItemsFilter.filter,
              customerSearchKeyword: null,
            },
          },
          fetchPolicy: 'network-only',
        }),
      );

      return result.data?.booking?.bookings;
    };

    /* 페이지를 나눠 받으면 경계에서 예약이 빠져 검증을 건너뛸 수 있어 한 번에 받는다 (fetchAllAtOnce 참고) */
    const firstPage = await fetchPage(1, bookingItemsFilter.size);
    const edges = await fetchAllAtOnce(firstPage, () => false, fetchPage);

    const blockedTicketIds = new Set<string>();

    edges.forEach(({ node }) => {
      /* 주문 전체가 취소요청/취소 진행 중인 경우 모든 티켓 차단 */
      if (canceledOrderStates.includes(node.order?.status)) {
        node.tickets?.forEach(ticket => blockedTicketIds.add(ticket.id));
        node.items?.forEach(item => {
          item.tickets?.forEach(ticket => blockedTicketIds.add(ticket.id));
        });
        return;
      }

      /* 부분 취소요청: 주문 아이템 dependedItems units(indexNo)에 해당하는 티켓 차단 */
      const canceledTicketIndexNoList =
        findCanceledTicketIndexNoListByNode(node);

      node.items?.forEach(item => {
        if (canceledTicketIndexNoList?.includes(item.indexNo)) {
          item.tickets?.forEach(ticket => blockedTicketIds.add(ticket.id));
        }
      });
    });

    return checkedTicketIdList.filter(id => !blockedTicketIds.has(id));
  };

  /* 불참 처리 api요청 */
  const onClickAbsent = async () => {
    if (!hasCheckedAttendance) {
      return;
    }

    const checkedTicketIdList = Object.values(attendanceCheckedList).flat();

    let flattedTicketIdList: string[];

    try {
      flattedTicketIdList =
        await excludeCancelRequestedTickets(checkedTicketIdList);
    } catch (error) {
      /* 주문 상태 검증에 실패하면 취소요청 건이 섞일 수 있으므로 처리 중단 */
      window.showToast(
        '주문 상태 확인에 실패했어요. 잠시 후 다시 시도해 주세요.',
        'failed',
        3000,
      );
      return;
    }

    if (flattedTicketIdList.length === 0) {
      window.showToast(
        '주문 취소가 요청된 예약이라 불참처리할 수 없어요.',
        'failed',
        3000,
      );
      setAttendanceCheckedList({});
      return;
    }

    if (flattedTicketIdList.length < checkedTicketIdList.length) {
      window.showToast(
        `주문 취소가 요청된 예약 ${
          checkedTicketIdList.length - flattedTicketIdList.length
        }건은 제외하고 불참처리해요.`,
        'default',
        3000,
      );
    }

    handleAbsentTickets(flattedTicketIdList, () => {
      HackleManager.logBookingAbsenceComplete(
        Number(bookingItems?.booking?.bookings?.edges[0].node.order.id),
        bookingItems?.booking?.bookings?.edges[0].node.customer.id,
        bookingItems?.booking?.bookings?.edges[0].node.info.product.title,
        bookingItems?.booking?.bookings?.edges[0].node.info.name,
        flattedTicketIdList,
        '예약상세',
      );
      handleRefetchBookingList();
      bookingListRefetch();

      setAttendanceCheckedList({});
    });
  };

  /* 참석 처리 api요청 */
  const onClickAttendance = async () => {
    if (!hasCheckedAttendance) {
      return;
    }

    const checkedTicketIdList = Object.values(attendanceCheckedList).flat();

    let flattedTicketIdList: string[];

    try {
      flattedTicketIdList =
        await excludeCancelRequestedTickets(checkedTicketIdList);
    } catch (error) {
      /* 주문 상태 검증에 실패하면 취소요청 건이 섞일 수 있으므로 처리 중단 */
      window.showToast(
        '주문 상태 확인에 실패했어요. 잠시 후 다시 시도해 주세요.',
        'failed',
        3000,
      );
      return;
    }

    if (flattedTicketIdList.length === 0) {
      window.showToast(
        '주문 취소가 요청된 예약이라 참석처리할 수 없어요.',
        'failed',
        3000,
      );
      setAttendanceCheckedList({});
      return;
    }

    if (flattedTicketIdList.length < checkedTicketIdList.length) {
      window.showToast(
        `주문 취소가 요청된 예약 ${
          checkedTicketIdList.length - flattedTicketIdList.length
        }건은 제외하고 참석처리해요.`,
        'default',
        3000,
      );
    }

    handleUseTickets(flattedTicketIdList, () => {
      HackleManager.logBookingAttendanceComplete(
        Number(bookingItems?.booking?.bookings?.edges[0].node.order.id),
        bookingItems?.booking?.bookings?.edges[0].node.customer.id,
        bookingItems?.booking?.bookings?.edges[0].node.info.product.title,
        bookingItems?.booking?.bookings?.edges[0].node.info.name,
        flattedTicketIdList,
        '예약상세',
      );
      handleRefetchBookingList();
      bookingListRefetch();

      setAttendanceCheckedList({});
    });
  };

  /* 불참 취소 api요청 */
  const onClickCancelAbsent = (checkedTicketIdList: string[]) => {
    handleRestoreTicketsAbsence(checkedTicketIdList, () => {
      HackleManager.logBookingAbsenceReverseComplete(
        Number(bookingItems?.booking?.bookings?.edges[0].node.order.id),
        bookingItems?.booking?.bookings?.edges[0].node.customer.id,
        bookingItems?.booking?.bookings?.edges[0].node.info.product.title,
        bookingItems?.booking?.bookings?.edges[0].node.info.name,
        checkedTicketIdList,
        '예약상세',
      );
      bookingListRefetch();
    });
  };

  /* 참석 취소 api요청 */
  const onClickCancelAttendance = (checkedTicketIdList: string[]) => {
    handleRestoreUseTickets(checkedTicketIdList, () => {
      HackleManager.logBookingAttendanceReverseComplete(
        Number(bookingItems?.booking?.bookings?.edges[0].node.order.id),
        bookingItems?.booking?.bookings?.edges[0].node.customer.id,
        bookingItems?.booking?.bookings?.edges[0].node.info.product.title,
        bookingItems?.booking?.bookings?.edges[0].node.info.name,
        checkedTicketIdList,
        '예약상세',
      );

      bookingListRefetch();
    });
  };

  /* 검색 인풋값 onChange 핸들러 */
  const keywordHandler = (value: string) => {
    setSearchKeyword(value);
  };

  /* 검색 핸들러 */
  const handleSearch = async () => {
    if (bookingItemsFilter.filter.customerSearchKeyword === searchKeyword) {
      return;
    }

    await bookingListRefetch({
      ...bookingItemsFilter,
      filter: {
        ...bookingItemsFilter.filter,
        customerSearchKeyword:
          searchKeyword.length === 0 ? null : searchKeyword,
      },
    });

    setBookingItemsFilter(pre => ({
      ...pre,
      filter: {
        ...pre.filter,
        customerSearchKeyword:
          searchKeyword.length === 0 ? null : searchKeyword,
      },
    }));
  };

  const attendedCount = userList.filter(
    ({ node: data }) => data.status === BookingState.USED,
  ).length;
  const absentedCount = userList.filter(
    ({ node: data }) => data.status === BookingState.ABSENT,
  ).length;

  const ticketType = !isFixedTicket ? 'unfixed' : 'fixed';

  return (
    <ModalBase
      isMobile={isMobile}
      handleModal={handleModal}
      useCustomHeader
      style={{ minHeight: isMobile ? '176px' : '748px' }}
    >
      <ModalContent>
        <AttendanceCheckModalHeader
          isMobile={isMobile}
          startTime={startTime}
          endTime={endTime}
          item={item}
          handleModal={handleModal}
          ticketType={ticketType}
        />
        <ModalBody>
          {loading ? (
            <ListLoadingSpinner />
          ) : (
            <UserListContent
              userList={userList}
              searchedKeyword={bookingItemsFilter.filter.customerSearchKeyword}
              searchKeyword={searchKeyword}
              onSearch={handleSearch}
              keywordHandler={keywordHandler}
              needToCheckAttendance
              checkAllUserHandler={checkAllUserHandler}
              checkUserHandler={checkUserHandler}
              checkedList={attendanceCheckedList}
              onClickCancelAbsent={onClickCancelAbsent}
              onClickCancelAttendance={onClickCancelAttendance}
              timeline={timeline}
              ticketType={ticketType}
              startTime={startTime}
              handleCancelBookingConfirmModal={handleCancelBookingConfirmModal}
              saveSelectedConfirmedBookingIds={saveSelectedConfirmedBookingIds}
            />
          )}

          {
            // userList.length >= 1 &&
            attendedCount + absentedCount === userList.length ? (
              <></>
            ) : (
              <ModalFooter>
                <ModalFooterButton
                  $isActive={hasCheckedAttendance}
                  data-type="absent"
                  onClick={onClickAbsent}
                >
                  불참
                </ModalFooterButton>
                <ModalFooterButton
                  $isActive={hasCheckedAttendance}
                  data-type="attendance"
                  onClick={onClickAttendance}
                >
                  참석
                </ModalFooterButton>
              </ModalFooter>
            )
          }
        </ModalBody>
      </ModalContent>

      {isCancelBookingConfirmModal && (
        <CancelBookingConfirmModal
          handleModal={handleCancelBookingConfirmModal}
          handleCancelBookingConfirm={handleCancelBookingConfirm}
        />
      )}
    </ModalBase>
  );
}

export default AttendanceCheckModal;
